<script setup>
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue';
import { OnClickOutside } from '@vueuse/components';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import ComboBoxDropdown from 'dashboard/components-next/combobox/ComboBoxDropdown.vue';

const props = defineProps({
  options: {
    type: Array,
    required: true,
    validator: value =>
      value.every(option => 'value' in option && 'label' in option),
  },
  placeholder: { type: String, default: '' },
  modelValue: { type: [String, Number], default: '' },
  disabled: { type: Boolean, default: false },
  searchPlaceholder: { type: String, default: '' },
  emptyState: { type: String, default: '' },
  message: { type: String, default: '' },
  hasError: { type: Boolean, default: false },
  useApiResults: { type: Boolean, default: false },
  useFixedDropdown: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue', 'search']);

const { t } = useI18n();

const selectedValue = ref(props.modelValue);
const open = ref(false);
const search = ref('');
const dropdownRef = ref(null);
const comboboxRef = ref(null);
const dropdownStyle = ref({});

const filteredOptions = computed(() => {
  if (props.useApiResults && search.value) {
    return props.options;
  }

  const searchTerm = search.value.toLowerCase();
  return props.options.filter(option =>
    option.label.toLowerCase().includes(searchTerm)
  );
});
const selectPlaceholder = computed(() => {
  return props.placeholder || t('COMBOBOX.PLACEHOLDER');
});
const selectedLabel = computed(() => {
  const selected = props.options.find(
    option => option.value === selectedValue.value
  );
  return selected?.label ?? selectPlaceholder.value;
});
const selectedOption = computed(() =>
  props.options.find(option => option.value === selectedValue.value) || null
);

const updateDropdownPosition = () => {
  if (!props.useFixedDropdown || !open.value || !comboboxRef.value) return;

  const spacing = 8;
  const preferredHeight = 320;
  const rect = comboboxRef.value.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  const availableBelow = viewportHeight - rect.bottom - spacing;
  const availableAbove = rect.top - spacing;
  const shouldOpenUp = availableBelow < preferredHeight && availableAbove > availableBelow;

  const width = Math.min(rect.width, viewportWidth - spacing * 2);
  const left = Math.min(
    Math.max(rect.left, spacing),
    viewportWidth - width - spacing
  );

  dropdownStyle.value = {
    left: `${Math.round(left)}px`,
    width: `${Math.round(width)}px`,
    top: shouldOpenUp
      ? `${Math.round(rect.top - spacing)}px`
      : `${Math.round(rect.bottom + spacing)}px`,
    transform: shouldOpenUp ? 'translateY(-100%)' : 'none',
  };
};

const bindPositionListeners = () => {
  if (!props.useFixedDropdown) return;
  window.addEventListener('resize', updateDropdownPosition);
  window.addEventListener('scroll', updateDropdownPosition, true);
};

const unbindPositionListeners = () => {
  if (!props.useFixedDropdown) return;
  window.removeEventListener('resize', updateDropdownPosition);
  window.removeEventListener('scroll', updateDropdownPosition, true);
};

const selectOption = option => {
  selectedValue.value = option.value;
  emit('update:modelValue', option.value);
  open.value = false;
  search.value = '';
};

const toggleDropdown = () => {
  if (props.disabled) return;
  open.value = !open.value;
  if (open.value) {
    search.value = '';
    nextTick(() => {
      updateDropdownPosition();
      dropdownRef.value?.focus();
    });
  }
};

watch(
  () => props.modelValue,
  newValue => {
    selectedValue.value = newValue;
  }
);

watch(open, value => {
  if (value) {
    bindPositionListeners();
    nextTick(updateDropdownPosition);
    return;
  }
  unbindPositionListeners();
});

onBeforeUnmount(() => {
  unbindPositionListeners();
});
</script>

<template>
  <div
    ref="comboboxRef"
    class="relative w-full min-w-0"
    :class="{
      'cursor-not-allowed': disabled,
      'group/combobox': !disabled,
    }"
    @click.prevent
  >
    <OnClickOutside @trigger="open = false">
      <Button
        variant="outline"
        :color="hasError && !open ? 'ruby' : open ? 'blue' : 'slate'"
        :disabled="disabled"
        no-animation
        class="justify-between w-full !px-3 !py-2.5 text-n-slate-12 font-normal group-hover/combobox:border-n-slate-6 focus:outline-n-brand"
        :class="{
          focused: open,
          '[&:not(.focused)]:dark:outline-n-weak [&:not(.focused)]:hover:enabled:outline-n-slate-6 [&:not(.focused)]:dark:hover:enabled:outline-n-slate-6':
            !hasError,
        }"
        @click="toggleDropdown"
      >
        <span class="inline-flex min-w-0 items-center gap-2">
          <span
            v-if="selectedOption?.icon"
            :class="[selectedOption.icon, 'size-4 shrink-0']"
            :style="{ color: selectedOption.color || 'currentColor' }"
          />
          <span class="truncate">{{ selectedLabel }}</span>
        </span>
        <span
          :class="[open ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down', 'size-4 shrink-0 text-n-slate-11']"
        />
      </Button>

      <ComboBoxDropdown
        ref="dropdownRef"
        v-model:search-value="search"
        :open="open"
        :options="filteredOptions"
        :search-placeholder="searchPlaceholder"
        :empty-state="emptyState"
        :selected-values="selectedValue"
        :use-fixed-position="useFixedDropdown"
        :dropdown-style="dropdownStyle"
        @search="emit('search', $event)"
        @select="selectOption"
      />

      <p
        v-if="message"
        class="mt-2 mb-0 text-xs truncate transition-all duration-500 ease-in-out"
        :class="{
          'text-n-ruby-9': hasError,
          'text-n-slate-11': !hasError,
        }"
      >
        {{ message }}
      </p>
    </OnClickOutside>
  </div>
</template>
